import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { compareSubcategories } from '../../utils/textUtils';
import './index.css';

const OtherSectionDetailPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // API Configuration
    const BASE_URL = 'https://www.whysocial.in/clap-kartel/public';
    const CRAFT_IMAGE_BASE_URL = 'https://whysocial.in/clap-kartel/public/uploads/cat-list';
    const BANNER_IMAGE_BASE_URL = 'https://whysocial.in/clap-kartel/public/uploads/banners';

    // Get section data from navigation state
    const { sectionId, sectionName, sectionImage, sourcePage } = location.state || {};

    // State management
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Video state
    const [videos, setVideos] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoIndex, setVideoIndex] = useState(0);
    const [isMuted, setIsMuted] = useState(true);

    // Banner state
    const [bannerData, setBannerData] = useState([]);
    const bannerStripRef = useRef(null);

    // Helper function to get authorization headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    // Helper function to get image URL
    const getImageUrl = (imageName) => {
        if (!imageName) return null;
        if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
            return imageName;
        }
        return `${CRAFT_IMAGE_BASE_URL}/${imageName}`;
    };

    // Helper to get banner image URL
    const getBannerImageUrl = (imageName) => {
        if (!imageName) return '';
        if (imageName.startsWith('http://') || imageName.startsWith('https://')) return imageName;
        return `${BANNER_IMAGE_BASE_URL}/${imageName}`;
    };

    // Helper to convert YouTube URL to embed URL
    const convertToEmbedUrl = (url, muted = true) => {
        if (!url) return '';
        if (url.includes('youtube.com/embed/')) return url;
        const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
        if (videoIdMatch && videoIdMatch[1]) {
            const muteParam = muted ? '1' : '0';
            return `https://www.youtube.com/embed/${videoIdMatch[1]}?autoplay=1&mute=${muteParam}&controls=1&modestbranding=1&rel=0`;
        }
        return url;
    };

    // Helper to get YouTube thumbnail
    const getYouTubeThumbnail = (url) => {
        if (!url) return null;
        const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
        if (videoIdMatch && videoIdMatch[1]) {
            return `https://img.youtube.com/vi/${videoIdMatch[1]}/hqdefault.jpg`;
        }
        return null;
    };

    // Helper function to convert text to title case
    const toTitleCase = (text) => {
        if (!text) return '';
        return text
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Fetch categories + videos + banners on mount
    useEffect(() => {
        if (!sectionId || !sectionName) {
            navigate('/other-section');
            return;
        }

        const fetchAll = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch categories
                const catResponse = await fetch(`${BASE_URL}/api/get-other-categories/${sectionId}`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (catResponse.ok) {
                    const catResult = await catResponse.json();
                    let foundCategories = [];
                    if (catResult?.crafts241 && Array.isArray(catResult.crafts241)) {
                        foundCategories = catResult.crafts241;
                    } else if (catResult?.data && Array.isArray(catResult.data)) {
                        foundCategories = catResult.data;
                    } else if (catResult?.categories && Array.isArray(catResult.categories)) {
                        foundCategories = catResult.categories;
                    } else if (Array.isArray(catResult)) {
                        foundCategories = catResult;
                    }
                    setCategories(foundCategories);
                }

                // Fetch videos
                const videoResponse = await fetch(`${BASE_URL}/getPodCastAll`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (videoResponse.ok) {
                    const videoResult = await videoResponse.json();
                    if (videoResult?.result && Array.isArray(videoResult.result) && videoResult.result.length > 0) {
                        setVideos(videoResult.result);
                        setSelectedVideo(videoResult.result[0]);
                    }
                }

                // Fetch banners
                const bannerResponse = await fetch(`${BASE_URL}/dash/getbanners/1`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (bannerResponse.ok) {
                    const bannerResult = await bannerResponse.json();
                    if (bannerResult?.banners && Array.isArray(bannerResult.banners)) {
                        setBannerData(bannerResult.banners);
                    } else if (Array.isArray(bannerResult)) {
                        setBannerData(bannerResult);
                    }
                }

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [sectionId, sectionName, navigate]);

    // Banner auto-scroll
    useEffect(() => {
        if (bannerData.length === 0) return;
        const interval = setInterval(() => {
            if (bannerStripRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = bannerStripRef.current;
                if (scrollLeft + clientWidth >= scrollWidth - 10) {
                    bannerStripRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    bannerStripRef.current.scrollBy({ left: 200, behavior: 'smooth' });
                }
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [bannerData]);

    // Handle video navigation
    const handlePrevVideo = () => {
        if (videos.length === 0) return;
        const newIndex = videoIndex > 0 ? videoIndex - 1 : videos.length - 1;
        setVideoIndex(newIndex);
        setSelectedVideo(videos[newIndex]);
    };

    const handleNextVideo = () => {
        if (videos.length === 0) return;
        const newIndex = videoIndex < videos.length - 1 ? videoIndex + 1 : 0;
        setVideoIndex(newIndex);
        setSelectedVideo(videos[newIndex]);
    };

    // Handle category row click
    const handleCategoryClick = (category) => {
        const innerDataValue = parseInt(category.innerData, 10);

        if (innerDataValue === 0) {
            navigate('/other-section-content', {
                state: {
                    subcatId: category.otherid,
                    innerdata: 'false',
                    categoryName: category.cat_name,
                    serviceName: sectionName,
                    catName: category.cat_name,
                    innerCatName: '',
                    sourcePage: sourcePage
                }
            });
        } else {
            navigate('/inner-data', {
                state: {
                    innerId: category.otherid,
                    catName: category.cat_name,
                    serviceName: sectionName,
                    sourcePage: sourcePage
                }
            });
        }
    };

    if (loading) {
        return (
            <div className="osd-page-wrapper">
                <div className="osd-loading-container">
                    <div className="osd-loading-spinner"></div>
                    <p>Loading {sectionName}...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="osd-page-wrapper">
                <div className="osd-loading-container">
                    <p style={{ color: '#e74c3c' }}>Error: {error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="osd-page-wrapper">
            {/* ===== BREADCRUMB NAVIGATION ===== */}
            <div className="osd-breadcrumb-header">
                <div className="osd-breadcrumb-content">
                    <span className="osd-back-arrow" onClick={() => navigate(-1)}>‹</span>
                    <h1 className="osd-breadcrumb-title">
                        <span className="osd-breadcrumb-link" onClick={() => {
                            if (sourcePage === '/') {
                                navigate('/', { state: { activeTab: 'other' } });
                            } else {
                                navigate(sourcePage || '/other-section');
                            }
                        }}>Other Sections</span>
                        <span className="osd-breadcrumb-sep">&gt;</span>
                        <span className="osd-breadcrumb-current">{toTitleCase(sectionName)}</span>
                    </h1>
                </div>
            </div>

            {/* ===== TWO-COLUMN LAYOUT ===== */}
            <div className="osd-two-column-layout">

                {/* LEFT COLUMN — Video + Banners */}
                <div className="osd-left-column">
                    {/* Video Carousel */}
                    <div className="osd-video-container">
                        {selectedVideo && (
                            <iframe
                                key={`${selectedVideo.id}-${isMuted}`}
                                src={convertToEmbedUrl(selectedVideo.video_url, isMuted)}
                                title={selectedVideo.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="osd-video-iframe"
                            />
                        )}
                        {videos.length > 1 && (
                            <>
                                <button className="osd-video-nav osd-video-nav-left" onClick={handlePrevVideo}>‹</button>
                                <button className="osd-video-nav osd-video-nav-right" onClick={handleNextVideo}>›</button>
                            </>
                        )}
                    </div>

                    {/* Banner Strip */}
                    {bannerData.length > 0 && (
                        <div className="osd-banner-strip" ref={bannerStripRef}>
                            {bannerData.map((banner, index) => {
                                const imgSrc = getBannerImageUrl(banner?.banner_img || banner?.image || banner?.banner_image || banner?.url);
                                return (
                                    <div key={`banner-${index}`} className="osd-banner-card">
                                        <img
                                            src={imgSrc}
                                            alt={banner?.title || `Banner ${index + 1}`}
                                            className="osd-banner-img"
                                            onError={(e) => { e.target.style.opacity = '0.3'; }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* RIGHT COLUMN — Category Cards */}
                <div className="osd-right-column">
                    <div className="osd-categories-card">
                        {categories.length > 0 ? (
                            <div className="osd-category-grid">
                                {[...categories]
                                    .sort((a, b) => compareSubcategories(a, b, 'cat_name'))
                                    .map((category, index) => (
                                    <motion.div
                                        key={`${index}_${category.id || index}`}
                                        className="osd-category-item"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.03 }}
                                        whileHover={{ scale: 1.02 }}
                                        onClick={() => handleCategoryClick(category)}
                                    >
                                        <span className="osd-category-name">
                                            {category.cat_name ? category.cat_name.toUpperCase() : ''}
                                        </span>
                                        <span className="osd-category-arrow">&gt;</span>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <div className="osd-no-categories">
                                <p>No categories available for {sectionName}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OtherSectionDetailPage;
